<template>
  <DSFormField
    :id="id"
    :name="props.name"
    :label="props.label ?? undefined"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
    :is-group="props.showNumberField"
    :required="props.required"
    :disabled="props.disabled"
    :helper-text="props.helperText"
    :error-messages="dsFormField.errorMessages.value"
  >
    <template #default="{ describedBy }">
      <div
        ref="rootRef"
        :class="rootClasses"
        :style="sliderStyle"
        data-cy="ds-slider-input"
        @focus.capture="onFocus"
        @blur.capture="onBlur"
      >
        <div :class="DS_SLIDER_INPUT_CLASSES.Controls">
          <DSSliderInput
            :id="id"
            ref="sliderInputRef"
            :name="props.name"
            :model-value="currentValue"
            :min="resolvedMin"
            :max="resolvedMax"
            :step="resolvedStep"
            :disabled="props.disabled"
            :required="props.required"
            :accessible-label="props.showNumberField || !props.label ? accessibleLabel : undefined"
            :has-error="hasErrors"
            :described-by="describedBy"
            :show-tick-marks="props.showTickMarks"
            :tick-mark-styles="tickMarkStyles"
            :show-thumb-icon="isHighContrast"
            @update:model-value="setValue"
            @change="applyDraft"
          />

          <DSSliderNumberInput
            v-if="props.showNumberField"
            :id="numberInputId"
            :model-value="currentValue"
            :disabled="props.disabled"
            :min="resolvedMin"
            :max="resolvedMax"
            :step="resolvedStep"
            :has-error="hasErrors"
            :accessible-label="accessibleLabel"
            :described-by="describedBy"
            :stepper-controls="stepperControls"
            @update:model-value="setValue"
            @change="applyDraft"
            @step="stepBy"
          />
        </div>
      </div>
    </template>
  </DSFormField>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import DSSliderInput from "@/src/design_system/form_elements/slider_input/components/DSSliderInput.vue";
import DSSliderNumberInput from "@/src/design_system/form_elements/slider_input/components/DSSliderNumberInput.vue";
import { useDSSliderInputValue } from "@/src/design_system/form_elements/slider_input/composables/useDSSliderInputValue";
import { DS_SLIDER_INPUT_CLASSES } from "@/src/design_system/form_elements/slider_input/constants/classes";
import type {
  DSSliderFieldEmits,
  DSSliderFieldModelValue,
  DSSliderFieldProps,
} from "@/src/design_system/form_elements/slider_input/types";
import { useThemeStore } from "@/src/design_system/shared/store/theme";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";

const props = withDefaults(defineProps<DSSliderFieldProps>(), {
  hiddenLabel: false,
  label: undefined,
  tooltip: undefined,
  disabled: false,
  required: false,
  min: 0,
  max: 100,
  step: 1,
  showNumberField: true,
  showTickMarks: false,
  helperText: undefined,
  errorMessages: undefined,
  rules: undefined,
});

const [modelValue, modelModifiers] = defineModel<DSSliderFieldModelValue, "lazy">({ default: 0 });
const emit = defineEmits<DSSliderFieldEmits>();

// With `v-model.lazy` the controls adjust this draft and the model follows once a control applies it.
const draft = ref<DSSliderFieldModelValue>();
const sourceValue = computed(() => draft.value ?? modelValue.value);

const sliderInputRef = ref<InstanceType<typeof DSSliderInput>>();
const rootRef = ref<HTMLElement>();
let hasFocus = false;
let restoringFocus = false;
let deferredBlur: FocusEvent | undefined;
const { focus } = useDSInput(sliderInputRef);
const cancelFocusRestoration = () => {
  if (restoringFocus) hasFocus = false;
  restoringFocus = false;
  const event = deferredBlur;
  deferredBlur = undefined;
  if (event) emit("blur", event);
};
const blur = () => {
  cancelFocusRestoration();
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) activeElement.blur();
};
useEventListener(
  document,
  "focus",
  event => {
    if (restoringFocus && event.target instanceof Node && !rootRef.value?.contains(event.target)) {
      cancelFocusRestoration();
    }
  },
  true
);
const { id } = useDSFormFieldId({ props });
const { isHighContrast } = useThemeStore();

const {
  currentValue,
  normalizeValue,
  resolvedMax,
  resolvedMin,
  resolvedStep,
  sliderStyle,
  stepperControls,
  tickMarkStyles,
} = useDSSliderInputValue(props, sourceValue);

const dsFormField = useDSFormField<DSSliderFieldModelValue>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: currentValue,
  rules: computed(() => props.rules),
});

const hasErrors = computed(() => !!dsFormField.errorMessages.value.length);
const accessibleLabel = computed(() => props.label || props.name);
const numberInputId = computed(() => `${id.value}-number`);

watch(
  () => normalizeValue(modelValue.value),
  value => {
    if (!Object.is(modelValue.value, value)) {
      modelValue.value = value;
    }
  },
  { immediate: true }
);

watch(modelValue, () => {
  draft.value = undefined;
});

watch([() => props.showNumberField, () => props.disabled], async ([, disabled]) => {
  const shouldRestoreRangeFocus = !disabled && rootRef.value?.contains(document.activeElement);

  if (shouldRestoreRangeFocus) {
    restoringFocus = true;
    await nextTick();
    if (!restoringFocus) return;
    if (
      props.disabled ||
      (document.activeElement !== document.body && !rootRef.value?.contains(document.activeElement))
    ) {
      cancelFocusRestoration();
      return;
    }
    focus();
    restoringFocus = false;
    deferredBlur = undefined;
  }
});

const rootClasses = computed(() => ({
  [DS_SLIDER_INPUT_CLASSES.Root]: true,
  [DS_STATE_CLASSES.Disabled]: props.disabled,
  [DS_STATE_CLASSES.Error]: hasErrors.value,
}));

function setValue(value: number) {
  const normalizedValue = normalizeValue(value);

  if (modelModifiers.lazy) {
    draft.value = normalizedValue;
  } else if (!Object.is(modelValue.value, normalizedValue)) {
    modelValue.value = normalizedValue;
  }

  dsFormField.clearFieldErrors();
}

function applyDraft() {
  if (draft.value === undefined) return;

  const value = normalizeValue(draft.value);
  draft.value = undefined;

  if (!Object.is(modelValue.value, value)) {
    modelValue.value = value;
  }
}

function stepBy(direction: -1 | 1) {
  if (!props.disabled) {
    setValue(currentValue.value + resolvedStep.value * direction);
    applyDraft();
  }
}

function onFocus(event: FocusEvent) {
  if (event.relatedTarget instanceof Node && rootRef.value?.contains(event.relatedTarget)) return;
  if (hasFocus) return;
  hasFocus = true;
  emit("focus", event);
}

function onBlur(event: FocusEvent) {
  if (event.relatedTarget instanceof Node && rootRef.value?.contains(event.relatedTarget)) return;
  if (restoringFocus && !event.relatedTarget) {
    deferredBlur = event;
    return;
  }
  restoringFocus = false;
  deferredBlur = undefined;
  hasFocus = false;
  emit("blur", event);
}

defineExpose({
  focus,
  blur,
});
</script>
